import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Plus, Users as UsersIcon, Mail, Phone, Shield, Edit2, Key, AppWindow, AlertTriangle, Trash2, Building2, Settings2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import AccessAssignmentPanel from '@/components/AccessAssignmentPanel';

// App code to display name and color mapping
const APP_DISPLAY = {
  'CONTROL_ROOM': { name: 'Control Room', color: 'bg-purple-100 text-purple-800' },
  'INVENTORY': { name: 'Inventory', color: 'bg-green-100 text-green-800' },
  'PURCHASE': { name: 'Purchase', color: 'bg-orange-100 text-orange-800' },
  'KITCHEN': { name: 'Kitchen', color: 'bg-red-100 text-red-800' },
  'BAR': { name: 'Bar', color: 'bg-yellow-100 text-yellow-800' },
  '*': { name: 'All Apps', color: 'bg-indigo-100 text-indigo-800' },
};

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [userAllowedApps, setUserAllowedApps] = useState({});
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [banner, setBanner] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showAccessPanel, setShowAccessPanel] = useState(false);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [userFormData, setUserFormData] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    password: '',
  });
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: '',
  });
  const [roleFormData, setRoleFormData] = useState({
    user_id: '',
    org_id: '',
    outlet_id: '',
    role_code: '',
  });

  // Handler to open access panel
  const openAccessPanel = (user) => {
    setSelectedUserForAccess(user);
    setShowAccessPanel(true);
  };

  // Check if current user is admin (can delete)
  const isAdmin = currentUser?.is_super_admin || currentUser?.is_org_admin;
  const isSuperAdmin = currentUser?.is_super_admin === true;

  useEffect(() => {
    loadData();
  }, []);

  // Helper to normalize roles data (handles array, wrapped object, string, or other formats)
  const normalizeRoles = (data) => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && Array.isArray(data.roles)) return data.roles;
    if (typeof data === 'string') return [{ role_code: data, description: data }];
    return [];
  };

  const loadData = async () => {
    setLoading(true);
    setLoadError(null);
    setBanner(null);
    
    try {
      // Use Promise.allSettled to prevent one failing call from nuking the whole page
      const results = await Promise.allSettled([
        api.getUsers(),
        api.getRoles(),
        api.getUserRoles(),
        api.getOrganizations(),
        api.getOutlets(),
        api.getAllUsersAllowedApps(),
      ]);
      
      const [usersRes, rolesRes, userRolesRes, orgsRes, outletsRes, allowedAppsRes] = results;
      
      // Users is critical - if it fails, show error
      if (usersRes.status === 'rejected') {
        console.error('Users failed to load:', usersRes.reason);
        setLoadError('Failed to load users. Please try again.');
        setUsers([]);
      } else {
        setUsers(Array.isArray(usersRes.value?.data) ? usersRes.value.data : []);
      }
      
      // Non-critical dependencies: degrade gracefully + show banner
      if (rolesRes.status === 'fulfilled') {
        setRoles(normalizeRoles(rolesRes.value?.data));
      } else {
        setRoles([]);
        console.warn('Roles failed to load:', rolesRes.reason);
      }
      
      if (userRolesRes.status === 'fulfilled') {
        setUserRoles(Array.isArray(userRolesRes.value?.data) ? userRolesRes.value.data : []);
      } else {
        setUserRoles([]);
        console.warn('User roles failed to load:', userRolesRes.reason);
      }
      
      if (orgsRes.status === 'fulfilled') {
        const orgsData = orgsRes.value?.data;
        setOrganizations(Array.isArray(orgsData) ? orgsData : (orgsData?.organizations || []));
      } else {
        setOrganizations([]);
        console.warn('Organizations failed to load:', orgsRes.reason);
        setBanner('Organizations failed to load. Some actions may be unavailable.');
      }
      
      if (outletsRes.status === 'fulfilled') {
        const outletsData = outletsRes.value?.data;
        setOutlets(Array.isArray(outletsData) ? outletsData : (outletsData?.outlets || []));
      } else {
        setOutlets([]);
        console.warn('Outlets failed to load:', outletsRes.reason);
        setBanner((prev) => prev || 'Outlets failed to load. Some filters may be unavailable.');
      }
      
      if (allowedAppsRes.status === 'fulfilled') {
        setUserAllowedApps(allowedAppsRes.value?.data || {});
      } else {
        setUserAllowedApps({});
        console.warn('Allowed apps failed to load:', allowedAppsRes.reason);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      setLoadError(error?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update existing user
        const { password, ...updateData } = userFormData;
        await api.updateUser(editingUser.user_id, updateData);
      } else {
        // Create new user
        await api.createUser(userFormData);
      }
      setShowUserForm(false);
      setEditingUser(null);
      setUserFormData({ user_name: '', user_email: '', user_phone: '', password: '' });
      loadData();
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      user_name: user.user_name,
      user_email: user.user_email,
      user_phone: user.user_phone || '',
      password: '',
    });
    setShowUserForm(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('Passwords do not match!');
      return;
    }
    if (passwordData.new_password.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }
    try {
      await api.changePassword(selectedUserForPassword.user_id, passwordData.new_password);
      setShowPasswordForm(false);
      setSelectedUserForPassword(null);
      setPasswordData({ new_password: '', confirm_password: '' });
      alert('Password changed successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password');
    }
  };

  const handlePasswordClick = (user) => {
    setSelectedUserForPassword(user);
    setPasswordData({ new_password: '', confirm_password: '' });
    setShowPasswordForm(true);
  };

  const handleDeleteUser = async (user) => {
    setDeleting(true);
    try {
      await api.deleteUser(user.user_id);
      setShowDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.assignUserRole(roleFormData);
      setShowRoleForm(false);
      setRoleFormData({ user_id: '', org_id: '', outlet_id: '', role_code: '' });
      loadData();
    } catch (error) {
      console.error('Error assigning role:', error);
      alert('Failed to assign role');
    }
  };

  const getUserRolesList = (userId) => {
    // Ensure userRoles is an array before filtering
    const safeUserRoles = Array.isArray(userRoles) ? userRoles : [];
    const safeRoles = Array.isArray(roles) ? roles : [];
    
    return safeUserRoles
      .filter((ur) => ur && ur.user_id === userId)
      .map((ur) => {
        const role = safeRoles.find((r) => r && r.role_code === ur.role_code);
        return role ? role.role_code : (ur.role_code || 'Unknown');
      });
  };

  return (
    <div className="p-8" data-testid="users-page">
      {/* Warning Banner for partial load failures */}
      {banner && (
        <div className="mb-4 p-3 rounded-lg border border-amber-300 bg-amber-50 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            <span className="text-amber-800 text-sm">{banner}</span>
          </div>
          <button
            type="button"
            onClick={() => loadData()}
            className="px-3 py-1 text-sm bg-white border border-amber-300 rounded-lg hover:bg-amber-100"
          >
            Retry
          </button>
        </div>
      )}
      
      {/* Error Banner for critical failures */}
      {loadError && (
        <div className="mb-4 p-3 rounded-lg border border-red-300 bg-red-50 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-600" />
            <span className="text-red-800 text-sm">{loadError}</span>
          </div>
          <button
            type="button"
            onClick={() => loadData()}
            className="px-3 py-1 text-sm bg-white border border-red-300 rounded-lg hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Users & Roles</h1>
          <p className="text-gray-600 mt-2">Manage users and assign roles</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowRoleForm(true)}
            data-testid="assign-role-btn"
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Shield size={20} />
            Assign Role
          </button>
          <button
            onClick={() => setShowUserForm(true)}
            data-testid="create-user-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} />
            Create User
          </button>
        </div>
      </div>

      {/* Create/Edit User Form */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-testid="user-form">
            <h2 className="text-xl font-bold mb-4">{editingUser ? 'Edit User' : 'Create User'}</h2>
            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  data-testid="user-name-input"
                  value={userFormData.user_name}
                  onChange={(e) => setUserFormData({ ...userFormData, user_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  data-testid="user-email-input"
                  value={userFormData.user_email}
                  onChange={(e) => setUserFormData({ ...userFormData, user_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  data-testid="user-phone-input"
                  value={userFormData.user_phone}
                  onChange={(e) => setUserFormData({ ...userFormData, user_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    required={!editingUser}
                    data-testid="user-password-input"
                    value={userFormData.password}
                    onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                    placeholder="Minimum 6 characters"
                    minLength={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
              {editingUser && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    To change password, use the "Change Password" button on the user card.
                  </p>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  data-testid="submit-user"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingUser ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUserForm(false);
                    setEditingUser(null);
                    setUserFormData({ user_name: '', user_email: '', user_phone: '', password: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Form */}
      {showPasswordForm && selectedUserForPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-testid="password-form">
            <h2 className="text-xl font-bold mb-4">Change Password for {selectedUserForPassword.user_name}</h2>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password *
                </label>
                <input
                  type="password"
                  required
                  data-testid="new-password-input"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  placeholder="Minimum 6 characters"
                  minLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  required
                  data-testid="confirm-password-input"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  placeholder="Re-enter password"
                  minLength={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  data-testid="submit-password"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setSelectedUserForPassword(null);
                    setPasswordData({ new_password: '', confirm_password: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Role Form */}
      {showRoleForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-testid="role-form">
            <h2 className="text-xl font-bold mb-4">Assign Role</h2>
            <form onSubmit={handleRoleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User *</label>
                <select
                  required
                  data-testid="role-user-select"
                  value={roleFormData.user_id}
                  onChange={(e) => setRoleFormData({ ...roleFormData, user_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select User</option>
                  {users.map((user) => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.user_name} ({user.user_email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization *
                </label>
                <select
                  required
                  data-testid="role-org-select"
                  value={roleFormData.org_id}
                  onChange={(e) => setRoleFormData({ ...roleFormData, org_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.org_id} value={org.org_id}>
                      {org.org_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Outlet (Optional for org-wide roles)
                </label>
                <select
                  value={roleFormData.outlet_id}
                  onChange={(e) => setRoleFormData({ ...roleFormData, outlet_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">No Outlet (Org-wide)</option>
                  {outlets
                    .filter((o) => o.org_id === roleFormData.org_id)
                    .map((outlet) => (
                      <option key={outlet.outlet_id} value={outlet.outlet_id}>
                        {outlet.outlet_name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                <select
                  required
                  data-testid="role-code-select"
                  value={roleFormData.role_code}
                  onChange={(e) => setRoleFormData({ ...roleFormData, role_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={role.role_code} value={role.role_code}>
                      {role.role_code} - {role.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  data-testid="submit-role"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Assign
                </button>
                <button
                  type="button"
                  onClick={() => setShowRoleForm(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map((user) => (
            <div
              key={user.user_id}
              data-testid={`user-card-${user.user_email}`}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <UsersIcon className="text-purple-600" size={24} />
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs ${
                    user.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{user.user_name}</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p className="flex items-center gap-2">
                  <Mail size={14} />
                  {user.user_email}
                </p>
                {user.user_phone && (
                  <p className="flex items-center gap-2">
                    <Phone size={14} />
                    {user.user_phone}
                  </p>
                )}
                <div className="pt-2">
                  <p className="font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Shield size={14} />
                    Roles:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {getUserRolesList(user.user_id).map((role, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                      >
                        {role}
                      </span>
                    ))}
                    {getUserRolesList(user.user_id).length === 0 && (
                      <span className="text-gray-400 text-xs">No roles assigned</span>
                    )}
                  </div>
                </div>
                <div className="pt-2">
                  <p className="font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <AppWindow size={14} />
                    Allowed Apps:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {(userAllowedApps[user.user_id] || []).map((app, idx) => {
                      const appInfo = APP_DISPLAY[app] || { name: app, color: 'bg-gray-100 text-gray-800' };
                      return (
                        <span
                          key={idx}
                          className={`px-2 py-1 rounded text-xs ${appInfo.color}`}
                        >
                          {appInfo.name}
                        </span>
                      );
                    })}
                    {(!userAllowedApps[user.user_id] || userAllowedApps[user.user_id].length === 0) && (
                      <span className="text-gray-400 text-xs">No app access</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleEditUser(user)}
                  data-testid={`edit-user-${user.user_email}`}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <Edit2 size={16} />
                  Edit
                </button>
                <button
                  onClick={() => openAccessPanel(user)}
                  data-testid={`access-user-${user.user_email}`}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  title="Manage outlet scope, modules, and capabilities"
                >
                  <Settings2 size={16} />
                  Access
                </button>
                <button
                  onClick={() => handlePasswordClick(user)}
                  data-testid={`change-password-${user.user_email}`}
                  className="flex items-center justify-center gap-1 px-3 py-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 text-sm"
                  title="Change password"
                >
                  <Key size={16} />
                </button>
                {/* Delete button - Admin only, and can't delete yourself */}
                {isAdmin && user.user_id !== currentUser?.user_id && (
                  <button
                    onClick={() => setShowDeleteConfirm(user)}
                    data-testid={`delete-user-${user.user_email}`}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-sm"
                    title={isSuperAdmin ? "Delete user (SUPER_ADMIN)" : "Delete user (your org only)"}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              {/* Scope label for admins */}
              {isAdmin && (
                <div className="mt-2 flex justify-end">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 flex items-center gap-1">
                    {isSuperAdmin ? (
                      <>
                        <Shield size={8} />
                        Scope: All Orgs
                      </>
                    ) : (
                      <>
                        <Building2 size={8} />
                        Scope: Your Org
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="text-red-600" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete User</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800">
                Are you sure you want to delete <strong>{showDeleteConfirm.user_name}</strong> ({showDeleteConfirm.user_email})?
              </p>
              <ul className="text-xs text-red-600 mt-2 space-y-1">
                <li>• All role assignments will be removed</li>
                <li>• All org associations will be removed</li>
                <li>• User will lose access to all apps</li>
              </ul>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(showDeleteConfirm)}
                disabled={deleting}
                data-testid="confirm-delete-user-btn"
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Access Assignment Panel */}
      <AccessAssignmentPanel
        user={selectedUserForAccess}
        isOpen={showAccessPanel}
        onClose={() => {
          setShowAccessPanel(false);
          setSelectedUserForAccess(null);
        }}
        onSave={() => {
          // Optionally refresh user data after save
          loadData();
        }}
      />
    </div>
  );
};

export default Users;
